import { useState, useCallback, useEffect, useRef } from 'react';

import { operatorApiFetch } from '@services/operator/apiService';

// IDs in this repo are UUID strings, though older call sites used `number`.
// Keep both accepted at the type level — the queue treats this as opaque and
// just passes it through to the endpoint URL builders.
export type EntityId = string | number;

export type ChunkTask = {
  type: 'chunk';
  entityId: EntityId;
  sessionId: string;
  seq: number;
  blob: Blob;
};

export type FinalizeTask = {
  type: 'finalize';
  entityId: EntityId;
  sessionId: string;
  totalChunks: number;
  // Optional payload extension piped verbatim into the finalize POST body.
  // QC sets `parcel_id` and `video_index`; return verification omits both.
  extra?: Record<string, unknown>;
};

type Task = (ChunkTask | FinalizeTask) & { id: string; status: 'pending' | 'uploading' | 'completed' | 'failed' };

export interface UseChunkedUploadQueueOptions {
  signEndpoint: (entityId: EntityId) => string;
  finalizeEndpoint: (entityId: EntityId, sessionId: string) => string;
  maxQueueDepth?: number;
  onQueueOverflow?: () => void;
  onChunkUploadFailed?: (sessionId: string, seq: number, error: Error) => void;
  onFinalizeFailed?: (detail: { sessionId: string; missing: number[] }) => void;
  // `videoId` is the attached *_video record id returned by the finalize
  // endpoint (data.fulfillment_video_id for QC, data.return_receipt_video_id
  // for return verification). May be undefined if the response shape lacks it.
  onFinalizeSuccess?: (sessionId: string, videoId: number | string | undefined) => void;
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

export function useChunkedUploadQueue(options: UseChunkedUploadQueueOptions) {
  const {
    signEndpoint, finalizeEndpoint,
    maxQueueDepth = 60,
    onQueueOverflow, onChunkUploadFailed, onFinalizeFailed, onFinalizeSuccess,
  } = options;

  const [ queue, setQueue ] = useState<Task[]>([]);
  const queueRef = useRef<Task[]>([]);
  const isProcessingRef = useRef(false);

  const updateQueue = useCallback((updater: (q: Task[]) => Task[]) => {
    queueRef.current = updater(queueRef.current);
    setQueue(queueRef.current);
  }, []);

  const enqueueChunk = useCallback((task: Omit<ChunkTask, 'type'>): boolean => {
    const pending = queueRef.current.filter(t => t.status === 'pending' || t.status === 'uploading');
    if (pending.length >= maxQueueDepth) {
      onQueueOverflow?.();
      return false;
    }
    const id = `${task.sessionId}_${task.seq}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    updateQueue((q) => [ ...q, { ...task, type: 'chunk', id, status: 'pending' } ]);
    return true;
  }, [ maxQueueDepth, onQueueOverflow, updateQueue ]);

  const enqueueFinalize = useCallback((task: Omit<FinalizeTask, 'type'>): void => {
    const id = `finalize_${task.sessionId}_${Date.now()}`;
    updateQueue((q) => [ ...q, { ...task, type: 'finalize', id, status: 'pending' } ]);
  }, [ updateQueue ]);

  const processChunk = useCallback(async (task: ChunkTask): Promise<boolean> => {
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const signRes = await operatorApiFetch(signEndpoint(task.entityId), {
          method: 'POST',
          body: JSON.stringify({ session_id: task.sessionId, seq: task.seq }),
          headers: { 'Content-Type': 'application/json' },
        });
        if (!signRes.ok) throw new Error(`sign ${signRes.status}`);
        const { data: { url } } = await signRes.json();

        const putRes = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'video/webm' },
          body: task.blob,
        });
        if (!putRes.ok) throw new Error(`PUT ${putRes.status}`);
        return true;
      } catch (e) {
        if (attempt < 3) {
          await sleep(2 ** attempt * 250);
        } else {
          onChunkUploadFailed?.(task.sessionId, task.seq, e as Error);
          return false;
        }
      }
    }
    return false;
  }, [ signEndpoint, onChunkUploadFailed ]);

  const processFinalize = useCallback(async (task: FinalizeTask): Promise<boolean> => {
    try {
      const res = await operatorApiFetch(finalizeEndpoint(task.entityId, task.sessionId), {
        method: 'POST',
        body: JSON.stringify({
          total_chunks: task.totalChunks,
          ...(task.extra || {}),
        }),
        headers: { 'Content-Type': 'application/json' },
      });
      const body = await res.json();
      if (res.status === 422 && body.message === 'chunks_incomplete') {
        onFinalizeFailed?.({ sessionId: task.sessionId, missing: body.interpolations?.missing ?? [] });
        return false;
      }
      if (res.ok) {
        const videoId = body.data?.fulfillment_video_id ?? body.data?.return_receipt_video_id;
        onFinalizeSuccess?.(task.sessionId, videoId);
        return true;
      }
      onFinalizeFailed?.({ sessionId: task.sessionId, missing: [] });
      return false;
    } catch {
      onFinalizeFailed?.({ sessionId: task.sessionId, missing: [] });
      return false;
    }
  }, [ finalizeEndpoint, onFinalizeFailed, onFinalizeSuccess ]);

  useEffect(() => {
    if (isProcessingRef.current) return;
    const next = queue.find(t => t.status === 'pending');
    if (!next) return;

    isProcessingRef.current = true;
    updateQueue((q) => q.map(t => t.id === next.id ? { ...t, status: 'uploading' } : t));

    (async () => {
      try {
        const ok = next.type === 'chunk'
          ? await processChunk(next as ChunkTask)
          : await processFinalize(next as FinalizeTask);
        updateQueue((q) => q.map(t => t.id === next.id ? { ...t, status: ok ? 'completed' : 'failed' } : t));
      } finally {
        isProcessingRef.current = false;
      }
    })();
  }, [ queue, processChunk, processFinalize, updateQueue ]);

  const retryFailed = useCallback(() => {
    updateQueue((q) => q.map(t => t.status === 'failed' ? { ...t, status: 'pending' } : t));
  }, [ updateQueue ]);
  const clearCompleted = useCallback(() => {
    updateQueue((q) => q.filter(t => t.status !== 'completed'));
  }, [ updateQueue ]);

  const stats = {
    pending: queue.filter(t => t.status === 'pending').length,
    uploading: queue.filter(t => t.status === 'uploading').length,
    completed: queue.filter(t => t.status === 'completed').length,
    failed: queue.filter(t => t.status === 'failed').length,
  };

  return { enqueueChunk, enqueueFinalize, retryFailed, clearCompleted, stats };
}
